const TOKEN_KEY = "ignis_follow_token";
const SEEN_ALERTS_KEY = "ignis_seen_alert_ids";

export type FollowRecord = {
  kind: "incident" | "geofence";
  target: string;
  email: string | null;
  created_at: string;
};

/** Stable anonymous token in this browser. `?follow=` restores a shared magic link. */
export function followToken(): string {
  const fromUrl = new URLSearchParams(location.search).get("follow");
  if (fromUrl && /^[0-9a-f-]{8,64}$/i.test(fromUrl)) {
    localStorage.setItem(TOKEN_KEY, fromUrl);
    const clean = new URL(location.href);
    clean.searchParams.delete("follow");
    history.replaceState({}, "", clean);
    return fromUrl;
  }
  let token = localStorage.getItem(TOKEN_KEY);
  if (!token) {
    token = crypto.randomUUID();
    localStorage.setItem(TOKEN_KEY, token);
  }
  return token;
}

export function followShareUrl(): string {
  const url = new URL(location.origin);
  url.searchParams.set("follow", followToken());
  return url.toString();
}

export async function loadFollows(): Promise<FollowRecord[]> {
  const res = await fetch(`/api/follows?token=${encodeURIComponent(followToken())}`);
  if (!res.ok) return [];
  const body = (await res.json()) as { follows?: FollowRecord[] };
  return body.follows ?? [];
}

export async function setFollow(kind: FollowRecord["kind"], target: string, email?: string | null): Promise<boolean> {
  const res = await fetch("/api/follow", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ token: followToken(), kind, target, email: email ?? null }),
  });
  return res.ok;
}

export async function unsetFollow(kind: FollowRecord["kind"], target: string): Promise<boolean> {
  const q = new URLSearchParams({ token: followToken(), kind, target });
  const res = await fetch(`/api/follow?${q.toString()}`, { method: "DELETE" });
  return res.ok;
}

export async function loadAlerts(): Promise<import("../shared/types").AlertRow[]> {
  const res = await fetch(`/api/alerts?token=${encodeURIComponent(followToken())}`);
  if (!res.ok) return [];
  const body = (await res.json()) as { alerts?: import("../shared/types").AlertRow[] };
  return body.alerts ?? [];
}

export function readSeenAlertIds(): Set<string> {
  try {
    const raw = localStorage.getItem(SEEN_ALERTS_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

export function writeSeenAlertIds(ids: Set<string>): void {
  localStorage.setItem(SEEN_ALERTS_KEY, JSON.stringify([...ids].slice(0, 200)));
}

export async function ensureNotifyPermission(): Promise<boolean> {
  if (!("Notification" in window)) return false;
  if (Notification.permission === "granted") return true;
  if (Notification.permission === "denied") return false;
  const result = await Notification.requestPermission();
  return result === "granted";
}
